import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { DollarSignIcon, RepeatIcon, CalendarIcon, InfoIcon, CheckCircle2Icon, ShieldCheckIcon, AlertCircleIcon } from 'lucide-react';
import { AnimatedScreen, staggerContainer, staggerItem } from '../ui/AnimatedScreen';
import { OPENFINANCE_THEME } from '../../constants/openFinanceTheme';
import { getVRPFrequencyText, getVRPPurposeText, VRPPaymentPurpose } from '../../constants/uaeStandards';
import { VRPConfig } from './VRPSetupScreen';
export interface VRPPermissionScreenProps {
  merchantName: string;
  merchantLogo?: string;
  bankName: string;
  bankLogo: string;
  accountType: string;
  accountLastFour: string;
  vrpConfig: VRPConfig;
  currency: string;
  reference: string;
  paymentPurpose: VRPPaymentPurpose;
  onAuthorize: () => void;
  onCancel?: () => void;
}
export function VRPPermissionScreen({
  merchantName,
  merchantLogo,
  bankName,
  bankLogo,
  accountType,
  accountLastFour,
  vrpConfig,
  currency,
  reference,
  paymentPurpose,
  onAuthorize,
  onCancel
}: VRPPermissionScreenProps) {
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const totalMaxAmount = vrpConfig.frequency === 'one-time' ? vrpConfig.maxAmountPerPayment : vrpConfig.maxAmountPerPayment * vrpConfig.totalPaymentsAllowed;
  return <AnimatedScreen data-id="element-1298">
      <div className="space-y-6" data-id="element-1299">
        {/* Header */}
        <div data-id="element-1300">
          <h2 className="text-xl font-bold mb-2 leading-tight" style={{
          fontFamily: OPENFINANCE_THEME.fonts.display,
          color: OPENFINANCE_THEME.colors.primary700
        }} data-id="element-1301">
            Authorize Payment Permission
          </h2>
          <p className="text-sm leading-relaxed" style={{
          color: OPENFINANCE_THEME.colors.textLight,
          fontFamily: OPENFINANCE_THEME.fonts.body
        }} data-id="element-1302">
            Review the payment rules before authorizing
          </p>
        </div>

        {/* Merchant & Bank Info */}
        <motion.div initial={{
        opacity: 0,
        y: -10
      }} animate={{
        opacity: 1,
        y: 0
      }} className="p-4 border-2 rounded-2xl" style={{
        background: OPENFINANCE_THEME.gradients.card,
        borderColor: OPENFINANCE_THEME.colors.primary200
      }} data-id="element-1303">
          <div className="flex items-center justify-between" data-id="element-1304">
            {/* Merchant */}
            <div className="flex items-center gap-3" data-id="element-1305">
              {merchantLogo && <div className="w-12 h-12 rounded-xl flex items-center justify-center p-2" style={{
              backgroundColor: OPENFINANCE_THEME.colors.borderLight
            }} data-id="element-1306">
                  <img src={merchantLogo} alt={merchantName} className="w-full h-full object-contain" data-id="element-1307" />
                </div>}
              <div data-id="element-1308">
                <div className="font-bold text-base" style={{
                color: OPENFINANCE_THEME.colors.text
              }} data-id="element-1309">
                  {merchantName}
                </div>
                <div className="text-xs" style={{
                color: OPENFINANCE_THEME.colors.textMuted
              }} data-id="element-1310">
                  {getVRPPurposeText(paymentPurpose)}
                </div>
              </div>
            </div>

            {/* Bank */}
            <div className="flex items-center gap-3" data-id="element-1311">
              <div className="text-right" data-id="element-1312">
                <div className="font-bold text-sm" style={{
                color: OPENFINANCE_THEME.colors.text
              }} data-id="element-1313">
                  {bankName}
                </div>
                <div className="text-xs font-mono" style={{
                color: OPENFINANCE_THEME.colors.textMuted
              }} data-id="element-1314">
                  ****{accountLastFour}
                </div>
              </div>
              <div className="w-12 h-12 rounded-xl flex items-center justify-center p-2" style={{
              backgroundColor: OPENFINANCE_THEME.colors.borderLight
            }} data-id="element-1315">
                <img src={bankLogo} alt={bankName} className="w-full h-full object-contain" data-id="element-1316" />
              </div>
            </div>
          </div>
        </motion.div>

        {/* Payment Rules */}
        <motion.div variants={staggerContainer} initial="hidden" animate="show" data-id="element-1317">
          <div className="text-sm font-bold uppercase tracking-wide mb-3" style={{
          color: OPENFINANCE_THEME.colors.text
        }} data-id="element-1318">
            Payment Rules
          </div>

          <div className="space-y-3" data-id="element-1319">
            {/* Frequency */}
            <motion.div variants={staggerItem} className="p-4 rounded-xl border-2" style={{
            borderColor: OPENFINANCE_THEME.colors.border,
            backgroundColor: 'white'
          }} data-id="element-1320">
              <div className="flex items-center gap-3" data-id="element-1321">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{
                backgroundColor: OPENFINANCE_THEME.colors.primary50
              }} data-id="element-1322">
                  <RepeatIcon className="w-5 h-5" style={{
                  color: OPENFINANCE_THEME.colors.primary500
                }} data-id="element-1323" />
                </div>
                <div className="flex-1" data-id="element-1324">
                  <div className="text-xs font-medium mb-0.5" style={{
                  color: OPENFINANCE_THEME.colors.textMuted
                }} data-id="element-1325">
                    Payment Frequency
                  </div>
                  <div className="text-base font-bold" style={{
                  color: OPENFINANCE_THEME.colors.text
                }} data-id="element-1326">
                    {getVRPFrequencyText(vrpConfig.frequency)}
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Max Amount */}
            <motion.div variants={staggerItem} className="p-4 rounded-xl border-2" style={{
            borderColor: OPENFINANCE_THEME.colors.border,
            backgroundColor: 'white'
          }} data-id="element-1327">
              <div className="flex items-center gap-3" data-id="element-1328">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{
                backgroundColor: OPENFINANCE_THEME.colors.accent500 + '15'
              }} data-id="element-1329">
                  <DollarSignIcon className="w-5 h-5" style={{
                  color: OPENFINANCE_THEME.colors.accent500
                }} data-id="element-1330" />
                </div>
                <div className="flex-1" data-id="element-1331">
                  <div className="text-xs font-medium mb-0.5" style={{
                  color: OPENFINANCE_THEME.colors.textMuted
                }} data-id="element-1332">
                    Maximum Per Payment
                  </div>
                  <div className="text-base font-bold" style={{
                  color: OPENFINANCE_THEME.colors.text
                }} data-id="element-1333">
                    {currency} {vrpConfig.maxAmountPerPayment.toLocaleString()}
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Total Payments */}
            {vrpConfig.frequency !== 'one-time' && <motion.div variants={staggerItem} className="p-4 rounded-xl border-2" style={{
            borderColor: OPENFINANCE_THEME.colors.border,
            backgroundColor: 'white'
          }} data-id="element-1334">
                <div className="flex items-center gap-3" data-id="element-1335">
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{
                backgroundColor: OPENFINANCE_THEME.colors.primary50
              }} data-id="element-1336">
                    <CalendarIcon className="w-5 h-5" style={{
                  color: OPENFINANCE_THEME.colors.primary500
                }} data-id="element-1337" />
                  </div>
                  <div className="flex-1" data-id="element-1338">
                    <div className="text-xs font-medium mb-0.5" style={{
                  color: OPENFINANCE_THEME.colors.textMuted
                }} data-id="element-1339">
                      Total Payments Allowed
                    </div>
                    <div className="text-base font-bold" style={{
                  color: OPENFINANCE_THEME.colors.text
                }} data-id="element-1340">
                      {vrpConfig.totalPaymentsAllowed} payments
                    </div>
                  </div>
                </div>
              </motion.div>}

            {/* Total Maximum */}
            <motion.div variants={staggerItem} className="p-4 rounded-xl" style={{
            background: `linear-gradient(135deg, ${OPENFINANCE_THEME.colors.accent500}15 0%, ${OPENFINANCE_THEME.colors.accent600}15 100%)`,
            border: `2px solid ${OPENFINANCE_THEME.colors.accent500}30`
          }} data-id="element-1341">
              <div className="flex items-center justify-between" data-id="element-1342">
                <div data-id="element-1343">
                  <div className="text-xs font-medium mb-0.5" style={{
                  color: OPENFINANCE_THEME.colors.textMuted
                }} data-id="element-1344">
                    Maximum Total Amount
                  </div>
                  <div className="text-2xl font-bold" style={{
                  color: OPENFINANCE_THEME.colors.accent600
                }} data-id="element-1345">
                    {currency} {totalMaxAmount.toLocaleString()}
                  </div>
                </div>
                <div className="text-xs text-right" style={{
                color: OPENFINANCE_THEME.colors.textMuted
              }} data-id="element-1346">
                  {vrpConfig.frequency === 'one-time' ? 'Single payment' : <>
                      {vrpConfig.maxAmountPerPayment.toLocaleString()} ×{' '}
                      {vrpConfig.totalPaymentsAllowed}
                    </>}
                </div>
              </div>
            </motion.div>
          </div>
        </motion.div>

        {/* Balance Check Permission */}
        <motion.div initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.3
      }} className="p-4 rounded-xl" style={{
        backgroundColor: OPENFINANCE_THEME.colors.primary50,
        border: `2px solid ${OPENFINANCE_THEME.colors.primary200}`
      }} data-id="element-1347">
          <div className="flex items-start gap-3" data-id="element-1348">
            <CheckCircle2Icon className="w-5 h-5 flex-shrink-0 mt-0.5" style={{
            color: OPENFINANCE_THEME.colors.primary500
          }} data-id="element-1349" />
            <div className="text-sm" style={{
            color: OPENFINANCE_THEME.colors.primary700
          }} data-id="element-1350">
              <div className="font-semibold mb-1" data-id="element-1351">Balance Check Permission</div>
              <div className="text-xs opacity-90" data-id="element-1352">
                {merchantName} will check your account balance before each
                payment to ensure sufficient funds.
              </div>
            </div>
          </div>
        </motion.div>

        {/* Your Rights */}
        <motion.div initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.4
      }} className="p-4 rounded-xl" style={{
        backgroundColor: OPENFINANCE_THEME.colors.success + '10',
        border: `2px solid ${OPENFINANCE_THEME.colors.success}30`
      }} data-id="element-1353">
          <div className="flex items-start gap-3" data-id="element-1354">
            <ShieldCheckIcon className="w-5 h-5 flex-shrink-0 mt-0.5" style={{
            color: OPENFINANCE_THEME.colors.success
          }} data-id="element-1355" />
            <div className="text-sm" style={{
            color: OPENFINANCE_THEME.colors.success
          }} data-id="element-1356">
              <div className="font-semibold mb-1" data-id="element-1357">Your Rights</div>
              <ul className="text-xs space-y-1 opacity-90" data-id="element-1358">
                <li data-id="element-1359">• Pause payments at any time</li>
                <li data-id="element-1360">• Cancel this permission whenever you want</li>
                <li data-id="element-1361">• View complete payment history</li>
                <li data-id="element-1362">• Payments will never exceed your set limits</li>
              </ul>
            </div>
          </div>
        </motion.div>

        {/* Terms Agreement */}
        <motion.div initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.5
      }} data-id="element-1363">
          <label className="flex items-start gap-3 cursor-pointer" data-id="element-1364">
            <input type="checkbox" checked={agreedToTerms} onChange={e => setAgreedToTerms(e.target.checked)} className="mt-1 w-5 h-5 rounded border-2 cursor-pointer" style={{
            accentColor: OPENFINANCE_THEME.colors.primary500
          }} data-id="element-1365" />
            <span className="text-sm leading-relaxed" style={{
            color: OPENFINANCE_THEME.colors.text
          }} data-id="element-1366">
              I authorize {merchantName} to initiate payments from my{' '}
              {accountType} account (****{accountLastFour}) according to the
              rules above. I understand I can cancel this permission at any
              time.
            </span>
          </label>
        </motion.div>

        {/* Warning if not agreed */}
        {!agreedToTerms && <motion.div initial={{
        opacity: 0,
        height: 0
      }} animate={{
        opacity: 1,
        height: 'auto'
      }} className="p-3 rounded-xl flex items-start gap-2" style={{
        backgroundColor: OPENFINANCE_THEME.colors.warning + '15',
        border: `1px solid ${OPENFINANCE_THEME.colors.warning}30`
      }} data-id="element-1367">
            <AlertCircleIcon className="w-4 h-4 flex-shrink-0 mt-0.5" style={{
          color: OPENFINANCE_THEME.colors.warning
        }} data-id="element-1368" />
            <div className="text-xs" style={{
          color: OPENFINANCE_THEME.colors.warning
        }} data-id="element-1369">
              Please review and agree to the terms above to continue
            </div>
          </motion.div>}

        {/* Actions */}
        <div className="space-y-3 pt-2" data-id="element-1370">
          {/* Cancel Button with gradient border */}
          {onCancel && <button onClick={onCancel} className="relative w-full px-6 py-4 bg-white font-semibold transition-all hover:bg-gray-50 overflow-hidden" style={{
          borderRadius: '50px'
        }} data-id="element-1371">
              {/* Gradient border effect */}
              <div className="absolute inset-0 rounded-full p-[2px]" style={{
            background: 'linear-gradient(135deg, #00BCD4 0%, #1976D2 100%)',
            WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
            WebkitMaskComposite: 'xor',
            maskComposite: 'exclude'
          }} data-id="element-1372" />
              <span className="relative z-10" style={{
            background: 'linear-gradient(135deg, #00BCD4 0%, #1976D2 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text'
          }} data-id="element-1373">
                Cancel
              </span>
            </button>}

          {/* Authorize Button with gradient background and ALTAREQ logo */}
          <button onClick={onAuthorize} disabled={!agreedToTerms} className="w-full px-6 py-4 text-white font-semibold transition-all hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed" style={{
          background: agreedToTerms ? 'linear-gradient(90deg, #00BCD4 0%, #1976D2 50%, #000000 100%)' : '#E5E7EB',
          borderRadius: '50px'
        }} data-id="element-1374">
            <div className="flex items-center justify-center gap-3" data-id="element-1375">
              <img src="https://cdn.magicpatterns.com/uploads/8Zyr2eTymP8hQsszbaoPhJ/White_Mark.svg" alt="ALTAREQ" className="h-6 w-6 object-contain" data-id="element-1376" />
              <span data-id="element-1377">Authorize using Al Tareq</span>
            </div>
          </button>
        </div>
      </div>
    </AnimatedScreen>;
}